import { Table } from '@components/Common/index';
import { BookkeepingEntry } from '@api/queries/inventory';
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Column, Filter, RowInfo, SortingRule } from 'react-table';
import {
  getAmountColumn,
  getDirectionColumn,
  getEntrySourceColumn,
  getEntryStateColumn,
  getEntryTypeColumn,
  getUnloadingFinishedColumn
} from '../../Common/entryColumns';

interface Props {
  onEntrySelected: (entry: BookkeepingEntry) => void;
  externalFiltersKey: string;
  entries: BookkeepingEntry[];
  selectedEntryId?: string;
}

interface TableState {
  page: number;
  pageSize: number;
  filtered: Filter[];
  sorted: SortingRule[];
}

const DEFAULT_TABLE_STATE: TableState = {
  page: 0,
  pageSize: 25,
  filtered: [],
  sorted: [{ id: 'unloadingFinished', desc: true }]
};

const EntryTable: React.FC<Props> = ({
  onEntrySelected,
  externalFiltersKey,
  entries,
  selectedEntryId
}) => {
  const { t } = useTranslation();
  const [tableState, setTableState] = useState<TableState>(DEFAULT_TABLE_STATE);
  const [prevFiltersKey, setPrevFiltersKey] = useState(externalFiltersKey);

  // Reset to first page when external filters change (state adjustment during rendering)
  if (externalFiltersKey !== prevFiltersKey) {
    setPrevFiltersKey(externalFiltersKey);
    setTableState(prev => ({ ...prev, page: 0 }));
  }

  const handleRowDoubleClick = useCallback((rowInfo: RowInfo) => {
    if (onEntrySelected) {
      onEntrySelected(rowInfo.original);
    }
  }, [onEntrySelected]);

  const handlePageChange = useCallback((newPage: number) => {
    setTableState(prev => ({ ...prev, page: newPage }));
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number, newPage: number) => {
    setTableState(prev => ({ ...prev, pageSize: newPageSize, page: newPage }));
  }, []);

  const handleFilteredChange = useCallback((newFiltered: Filter[]) => {
    setTableState(prev => ({ ...prev, filtered: newFiltered, page: 0 }));
  }, []);

  const handleSortedChange = useCallback((newSorted: SortingRule[]) => {
    setTableState(prev => ({ ...prev, sorted: newSorted, page: 0 }));
  }, []);

  const columns: Column[] = [
    {
      Header: 'Id',
      id: 'id',
      accessor: 'id',
      show: false
    },
    getEntrySourceColumn(t),
    getEntryTypeColumn(t),
    {
      Header: t('entry.terminal'),
      id: 'terminalName',
      maxWidth: 150,
      accessor: 'terminalName'
    },
    {
      Header: t('entry.customer'),
      id: 'customerName',
      accessor: 'customerName'
    },
    {
      Header: t('entry.tank'),
      id: 'tankName',
      maxWidth: 120,
      accessor: 'tankName'
    },
    {
      Header: t('entry.product'),
      id: 'productName',
      accessor: (entry: BookkeepingEntry) => {
        return entry.productName ? `${entry.productName} (${entry.productCode})` : '';
      }
    },
    getEntryStateColumn(t),
    getAmountColumn(t),
    getDirectionColumn(t),
    getUnloadingFinishedColumn(t),
    {
      Header: t('entry.carrier-information'),
      id: 'carrierInformation',
      accessor: 'carrierInformation'
    },
    {
      Header: t('entry.notes'),
      id: 'notes',
      accessor: 'notes'
    }
  ];

  return (
    <Table
      data={entries}
      columns={columns}
      defaultSortColumn="unloadingFinished"
      defaultSortDesc={true}
      defaultPageSize={25}
      showPagination={true}
      showPageSizeOptions={true}
      filterable={true}
      onDoubleClick={handleRowDoubleClick}
      rowSelector={i => selectedEntryId === i.original.id}
      noDataText={t('entry.no_rows_bookkeeping_entries')}
      managed={{
        page: tableState.page,
        pageSize: tableState.pageSize,
        filtered: tableState.filtered,
        sorted: tableState.sorted,
        onPageChange: handlePageChange,
        onPageSizeChange: handlePageSizeChange,
        onFilteredChange: handleFilteredChange,
        onSortedChange: handleSortedChange
      }}
    />
  );
};

export default EntryTable;
